import React, {useEffect, useState} from "react";

import {clearCache, withCache} from "../cache";
import {useApi} from "../PlayMakersProvider";
import {CreateReward, ExtendedReward, Reward, UpdateReward, UserReward} from "../types";


type CreateRewardMethod = (r: CreateReward) => Promise<Reward>;
type FetchRewardMethod = (quiet?: boolean, refresh?: boolean) => Promise<Reward>;
type UpdateRewardMethod = (r: UpdateReward) => Promise<Reward>;
type RedeemRewardMethod = () => Promise<UserReward>;
type DeleteRewardMethod = () => Promise<void>;

interface UseRewardReturn {
  createReward: CreateRewardMethod
  deleteReward: DeleteRewardMethod
  refresh: FetchRewardMethod
  redeemReward: RedeemRewardMethod
  updateReward: UpdateRewardMethod
  setReward: React.Dispatch<React.SetStateAction<Reward | null>>;
  reward: Reward | null;
  fetched: boolean;
}

export const useReward = (id?: string): UseRewardReturn => {
  const {
    getReward,
    createReward: createRewardApi,
    updateReward: updateRewardApi,
    deleteReward: deleteRewardApi,
    redeemReward: redeemRewardApi
  } = useApi();

  const [reward, setReward] = useState<Reward | null>(null);
  const [fetched, setFetched] = useState<boolean>(false);

  const fetchReward: FetchRewardMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getReward", [id]);

    try {
      if(!id) throw new Error("No reward to fetch");

      const fetchedReward = await withCache(getReward, [id], 120_000);
      setReward(fetchedReward);

      return fetchedReward;
    } catch (err) {
      console.error("Error fetching reward", err);
      throw new Error("Error fetching reward", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  const createReward: CreateRewardMethod = async r => {
    const newReward = {...reward, ...r};

    if(!newReward) throw new Error("No reward to create");

    try {
      const createdReward = await createRewardApi(newReward);
      setReward(createdReward);

      clearCache("getReward", [id]);
      clearCache("getRewardsByProject");

      return createdReward;
    } catch (err) {
      console.error("Error while creating reward", err);
      throw new Error("Error while creating reward", {cause: err});
    }
  };

  const redeemReward: RedeemRewardMethod = async() => {
    try {
      const rewardId = reward?.id || id;

      if(!rewardId) throw new Error("No reward to redeem");

      const redeemedUserReward = await redeemRewardApi(rewardId);

      clearCache("getReward", [id]);
      clearCache("getRewardsByProject");

      return redeemedUserReward;
    } catch (err) {
      console.error("Error redeeming reward", err);
      throw new Error("Error redeeming reward", {cause: err});
    }
  };

  const updateReward: UpdateRewardMethod = async r => {
    try {
      if(!reward?.id) throw new Error("No reward to update");

      const updatedReward = await updateRewardApi(reward.id, r);
      setReward(updatedReward);

      clearCache("getReward", [id]);
      clearCache("getRewardsByProject");

      return updatedReward;
    } catch (err) {
      console.error("Error updating reward", err);
      throw new Error("Error updating reward", {cause: err});
    }
  };

  const deleteReward: DeleteRewardMethod = async() => {
    try {
      if(!reward?.id) throw new Error("No reward to delete");

      await deleteRewardApi(reward.id);
      setReward(null);

      clearCache("getReward", [id]);
      clearCache("getRewardsByProject");
    } catch (err) {
      console.error("Error deleting reward", err);
      throw new Error("Error deleting reward", {cause: err});
    }
  };

  useEffect(() => {
    if(id) fetchReward();
  }, [id]);

  return {
    createReward,
    deleteReward,
    fetched,
    redeemReward,
    refresh: quiet => fetchReward(quiet, true),
    reward,
    setReward,
    updateReward
  };
};

type FetchRewardsMethod = (quiet?: boolean, refresh?: boolean) => Promise<ExtendedReward[]>;
type DeleteRewardsMethod = (id: string) => Promise<void>;
type PushRewardsMethod = (id: string, reward: UpdateReward) => Promise<Reward>;
type RedeemRewardsMethod = (id: string) => Promise<UserReward>;
interface UseRewardsReturn {
  fetched: boolean;
  rewards: ExtendedReward[];
  refresh: FetchRewardsMethod;

  // @deprecated use rewards instead
  rewardList: ExtendedReward[];
}

export const useRewards = (projectId?: string, params: {[key: string]: any} = {}): UseRewardsReturn  => {
  const {
    getRewardsByProject,
    updateReward: updateRewardApi,
    deleteReward: deleteRewardApi,
    redeemReward: redeemRewardApi
  } = useApi();

  const [rewards, setRewards] = useState<ExtendedReward[]>([]);
  const [fetched, setFetched] = useState<boolean>(false);

  const extendRewards = (rewards: Reward[]): ExtendedReward[] =>
    rewards.map(reward => ({
      ...reward,
      delete: () => deleteReward(reward.id),
      push: updates => pushReward(reward.id, updates),
      redeem: () => redeemReward(reward.id)
    }));


  const updateRewardState = (rewardId: string, updates: UpdateReward) =>
    setRewards(prevRewards =>
      prevRewards.map(reward =>
        reward.id === rewardId ? {...reward, ...updates} : reward
      )
    );


  const fetchRewards: FetchRewardsMethod = async(quiet = false, refresh) => {
    if(!quiet) setFetched(false);
    if(refresh) clearCache("getRewardsByProject");

    try {
      const rewards = extendRewards(await withCache(getRewardsByProject, [params, projectId], 120_000));
      setRewards(rewards);
      return rewards;
    } catch (err) {
      console.error("Error fetching rewards", err);
      throw new Error("Error fetching rewards", {cause: err});
    } finally {
      setFetched(true);
    }
  };

  const redeemReward: RedeemRewardsMethod = async id => {
    try {
      if(!id) throw new Error("No reward to redeem");

      const redeemedReward = await redeemRewardApi(id);

      clearCache("getRewardsByProject");

      return redeemedReward;
    } catch (err) {
      console.error("Error redeeming reward", err);
      throw new Error("Error redeeming reward", {cause: err});
    }
  };

  const pushReward: PushRewardsMethod = async(id, reward) => {
    try {
      const newReward = await updateRewardApi(id, reward);

      if(!newReward) throw new Error("Reward not uploaded");

      updateRewardState(id, newReward);

      clearCache("getRewardsByProject");

      return newReward;
    } catch (error) {
      console.error(`Error pushing reward '${id}'`, error);
      throw new Error(`Error pushing reward '${id}'`, {cause: error});
    }
  };

  const deleteReward: DeleteRewardsMethod = async(id: string) => {
    try {
      await deleteRewardApi(id);
      setRewards(prevRewards =>
        prevRewards.filter(reward => reward.id !== id)
      );

      clearCache("getRewardsByProject");
    } catch (error) {
      console.error(`Error deleting reward '${id}'`, error);
      throw new Error(`Error deleting reward '${id}'`, {cause: error});
    }
  };

  useEffect(() => {
    fetchRewards();
  }, [projectId, JSON.stringify(params)]);

  return {
    fetched,
    refresh: quiet => fetchRewards(quiet, true),
    rewardList: rewards,
    rewards
  };
};
